'use client';

import { useState } from 'react';
import Image from 'next/image';
import {
  PlaySVG,
  CloseIcon,
  SoundOffIcon,
  SoundOnIcon,
  DrawerCloseIcon,
  DrawerOpenIcon
} from './Icons';
import useVimeoPlayer from './use-vimeo-player';
import { findBiggestDiscount } from '@/utils/discounts';
import { useCampaign } from '@/app/CampaignContext';

export default function VimeoOverlayPlayer({ video, hideModal }) {
  const [showDrawer, setShowDrawer] = useState(true);
  const [message, setMessage] = useState(null);
  const videoSrc = `${video.video_url.split('?')[0]}?controls=0&autoplay=1&loop=1&muted=1`;
  const productThumb = video.product_thumbnail;
  const productInfo = video.product;

  const {
    iframeRef,
    playerRef,
    isLoaded,
    isMuted,
    isPaused,
    isBuffering,
    remountKey
  } = useVimeoPlayer(video);

  return (
    <div
      role="dialog"
      aria-modal={true}
      className={`vimeo-overlay-player relative aspect-[calc(9/16)] max-h-[100svh] max-w-full bg-white sm:h-full sm:max-h-[679px] sm:w-auto ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}
    >
      <iframe
        key={remountKey}
        src={videoSrc}
        ref={iframeRef}
        className="absolute inset-0 h-full w-full"
        allow="autoplay; fullscreen"
      />
      <aside
        className={`absolute inset-0 flex flex-col items-stretch justify-between ${isPaused ? 'bg-black/35' : 'bg-black/0'}`}
      >
        <PlayButtonOverlay
          playerRef={playerRef}
          isPaused={isPaused}
          isBuffering={isBuffering}
          videoUrl={video.video_url}
          setMessage={setMessage}
        />

        {isBuffering && <BufferingScreen />}

        <SocialLinkAndActions
          video={video}
          hideModal={hideModal}
          playerRef={playerRef}
          isMuted={isMuted}
        />

        {productInfo && (
          <ProductDrawer
            video={video}
            showDrawer={showDrawer}
            setShowDrawer={setShowDrawer}
            productInfo={productInfo}
            productThumb={productThumb}
          />
        )}

        {message && (
          <div
            className={
              'pointer-events-none absolute inset-0 flex items-center justify-center'
            }
          >
            <span className="body-l pointer-events-auto max-w-[245px] bg-black/80 px-3 py-1 text-white">
              {message}
            </span>
          </div>
        )}
      </aside>
    </div>
  );
}

/**
 * A very large play button that allows the entire screen to be clicked to play
 * or pause the video. (Order other important elements on top of this if important.)
 */
const PlayButtonOverlay = ({
  playerRef,
  isPaused,
  isBuffering,
  videoUrl,
  setMessage
}) => {
  const togglePlayback = async (playerRef, setMessage) => {
    if (playerRef.current) {
      const paused = await playerRef.current.getPaused();
      if (paused) {
        return playerRef.current.play().catch(() => {
          setMessage(
            <>
              <p>Looks like your browser might be blocking playback.</p>
              <p>
                Try watching with the sound muted, or{' '}
                <a className="underline" target="_blank" href={videoUrl}>
                  watch it directly on Vimeo.
                </a>
              </p>
            </>
          );
          setTimeout(() => {
            setMessage(null);
          }, 3000);
        });
      } else {
        return playerRef.current.pause();
      }
    }
  };

  return (
    <button
      className={`group absolute inset-0 flex items-center justify-center p-5 ${isPaused && !isBuffering ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}
      onClick={() => togglePlayback(playerRef, setMessage)}
    >
      <PlaySVG />
    </button>
  );
};

const BufferingScreen = () => {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-white">
      <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-b-transparent" />
    </div>
  );
};

const SocialLinkAndActions = ({ video, hideModal, playerRef, isMuted }) => {
  return (
    // Use flex row reverse to always position the actions on the right even
    // if the social link is not present.
    <div className="relative flex flex-row-reverse items-start justify-between bg-linear-to-t from-transparent to-[rgba(0,0,0,0.25)] p-3 text-white">
      <div className="flex flex-col items-end">
        <button className="group" onClick={(e) => hideModal(e)}>
          <CloseIcon className="pointer-events-none h-10 w-10" />
        </button>
        <button
          className="group mt-3"
          onClick={() => {
            if (playerRef.current) {
              playerRef.current.getMuted().then((muted) => {
                if (muted) {
                  playerRef.current.setMuted(false);
                } else {
                  playerRef.current.setMuted(true);
                }
              });
            }
          }}
        >
          {isMuted ? (
            <SoundOffIcon className="h-10 w-10" />
          ) : (
            <SoundOnIcon className="h-10 w-10" />
          )}
        </button>
      </div>
      {video.social_link?.href && video.social_link?.title && (
        <a
          className="body-l px-1 hover:underline"
          href={video.social_link.href}
        >
          {video.social_link.title}
        </a>
      )}
    </div>
  );
};

const ProductDrawer = ({
  productInfo,
  productThumb,
  video,
  showDrawer,
  setShowDrawer
}) => {
  const campaign = useCampaign();
  const priceInfo = getMinPriceInfo(productInfo, campaign);
  return (
    <div className="overflow-hidden bg-linear-to-t from-[rgba(0,0,0,0.5)] to-transparent pt-6">
      <div
        className={`relative p-4 ${showDrawer ? 'translate-y-0' : 'translate-y-full'} transition-transform duration-300 will-change-transform`}
      >
        <button
          className={`group absolute -top-5 right-4 ${showDrawer ? 'translate-y-0' : '-translate-y-8'} transition-transform duration-300 will-change-transform`}
          onClick={() => setShowDrawer((prev) => !prev)}
        >
          {showDrawer ? (
            <DrawerOpenIcon className="pointer-events-none h-8 w-8" />
          ) : (
            <DrawerCloseIcon className="pointer-events-none h-8 w-8" />
          )}
        </button>

        {productThumb ? (
          <div className="flex items-center justify-start">
            <a href={`/products/${productInfo.handle}`}>
              <Image
                src={productThumb.url}
                alt={
                  productThumb.description ??
                  productThumb.title.replace(/\.[^/.]+$/, '')
                }
                width={productThumb.dimensions?.width ?? 85}
                height={productThumb.dimensions?.height ?? 64}
              />
            </a>
            <div className="ml-2 text-white">
              <p className="video-product-title sh11 mb-2">
                <a href={`/products/${productInfo.handle}`}>
                  {productInfo.title}
                </a>
              </p>
              <PriceTag
                minPrice={priceInfo.minPrice}
                minComparePrice={priceInfo.minComparePrice}
                biggestDiscount={priceInfo.biggestDiscount}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-end justify-between text-white">
            <p className="video-product-title sh11">
              <a href={`/products/${productInfo.handle}`}>
                {productInfo.title}
              </a>
            </p>
            <PriceTag
              minPrice={priceInfo.minPrice}
              minComparePrice={priceInfo.minComparePrice}
            />
          </div>
        )}
        {video.button_text && productInfo.handle && (
          <a
            href={`/products/${productInfo.handle}`}
            className="btn-primary btn-s mt-4 inline-block w-full p-[9px] text-center"
          >
            {video.button_text}
          </a>
        )}
      </div>
    </div>
  );
};

const PriceTag = ({ minPrice, minComparePrice, biggestDiscount }) => {
  return (
    <p className="flex-shrink-0 [--price-color:white]">
      <span className="price-r-label">From:</span>&nbsp;
      <span className="price-s">{minPrice}</span>&nbsp;
      {minComparePrice && (
        <span className="price-s-crossed text-white">{minComparePrice}</span>
      )}
      {biggestDiscount && (
        <span className="badge-text-r ml-2 rounded-full border border-white px-2 py-0.5 whitespace-nowrap">
          {biggestDiscount}
        </span>
      )}
    </p>
  );
};

const getMinPriceInfo = (shopifyProduct, campaign) => {
  // The shopifyProduct object doesn't come with min price info so we need to
  // calculate it from the variants.
  const variants = shopifyProduct?.variants;

  if (!variants || variants.length === 0) {
    return { minPrice: 0, minComparePrice: 0 };
  }

  const lowestPriceVariant = variants.reduce((prev, current) => {
    return parseFloat(prev.price.amount) < parseFloat(current.price.amount)
      ? prev
      : current;
  }, variants[0]);

  const minPrice = parseFloat(lowestPriceVariant.price.amount);
  const minComparePrice = parseFloat(lowestPriceVariant.compareAtPrice.amount);

  const biggestDiscount = findBiggestDiscount(variants, campaign);

  return {
    minPrice: `$${minPrice.toFixed(0)}`,
    minComparePrice: `$${minComparePrice.toFixed(0)}`,
    biggestDiscount
  };
};
